import React from 'react';
import { Box, TextField, Typography, Alert, Snackbar } from '@mui/material';
import { AppConfig, ConfigItem } from '../../../services/configApi';
import { SectionSaveButtons } from './SectionSaveButtons';
import { useSectionConfig } from '../hooks/useSectionConfig';

interface CleanupConfigProps {
  config: AppConfig;
  items: ConfigItem[];
  disabled?: boolean;
}

const CLEANUP_KEYS: (keyof AppConfig)[] = ['log_retention_days'];

export const CleanupConfig: React.FC<CleanupConfigProps> = ({
  config,
  items,
  disabled = false,
}) => {
  const {
    currentValues,
    hasChanges,
    saving,
    updateValue,
    saveChanges,
    discardChanges,
  } = useSectionConfig(CLEANUP_KEYS, config);

  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const getValidationRules = (key: string) => {
    const item = items.find(configItem => configItem.key === key);
    return item?.validation_rules || {};
  };

  const handleSave = async () => {
    const result = await saveChanges();
    setNotification({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleDiscard = () => {
    discardChanges();
    setNotification({
      message: 'Changes discarded',
      type: 'success',
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Log Retention Days */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <TextField
            label="Log Retention"
            type="number"
            value={Number(currentValues.log_retention_days) || 0}
            onChange={e =>
              updateValue(
                'log_retention_days',
                parseInt(e.target.value, 10) || 0
              )
            }
            disabled={disabled || saving}
            sx={{ width: 150 }}
            inputProps={getValidationRules('log_retention_days')}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, flex: 1 }}
          >
            Days to keep all logs (queue jobs, process logs, blob logs) before
            deleting them
          </Typography>
        </Box>
      </Box>

      <SectionSaveButtons
        hasChanges={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        disabled={disabled}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
